import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import {
  Box,
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import axios from 'axios'
import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from 'react-i18next'
import ReactLoader from 'react-loader'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import editIcon from '@/assets/images/BlueEdit.svg'
import imageBg from '@/assets/images/ImageBg.svg'
import Button from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import type { PresignedUrlResponse } from '@/components/sidebar/Gallery'
import { useAppSelector } from '@/hooks/redux'
import {
  useLazyGetImagePresignedUrlQuery,
  useUploadCaseImagesMutation,
} from '@/store/apiService'
import { toggleImageSelection } from '@/store/project'
import {
  getProjectPermissions,
  getSupportedImagetypes,
  sortingData,
} from '@/utils/constants'
import { getDehazedImageUrl, imageType } from '@/utils/helper'

import ImageProgressBar from '../../components/ImageProgressBar'
import RecipeDescription from '../recipeDetail/RecipeDescription'
import RecipeDetailTable from '../recipeDetail/RecipeDetailTable'
import ImageActionMenu from './ImageActionMenu'
import ImageEditContainer from './ImageEditContainer'
import MultipleImageActions from './MultipleImageActions'

type ImageType = {
  id: string
  url: string
  name: string
  key_raw?: string
  image?: string
  note?: string
  latest_image?: string
  calibrated_image?: string
  type: string
  calibrate_meta?: {
    x: number
    y: number
    protocol: number
  }
}

type Props = {
  caseDetails: any
  ordering: string
  setOrdering: (order: string) => void
  refetch: () => any
  onUploadInitiated: (numFiles: number) => number
  onUploadStart: (numFiles: number) => void
  uploadingCount: number
  progressArray: number[]
  updateProgress: (index: number, value: number) => void
}

function ProjectImageGallery({
  caseDetails,
  ordering,
  setOrdering,
  refetch,
  onUploadInitiated,
  onUploadStart,
  uploadingCount,
  progressArray,
  updateProgress,
}: Props) {
  const [tab, setTab] = useState(0)
  const dispatch = useDispatch()

  const { t } = useTranslation('translation', {
    keyPrefix: 'ProjectDetails',
  })

  const { t: translation } = useTranslation()

  const handleSelect = (id: string) => {
    dispatch(toggleImageSelection({ imageId: id, caseId: caseDetails?.id }))
  }
  const images = caseDetails?.images
  const caseId = caseDetails?.id

  const selected = useAppSelector(
    (state) => state?.project?.selectedImageIds?.[caseId] || [],
  )
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null)
  const [menuImageId, setMenuImageId] = useState<string | null>(null)
  const [isImageEdit, setIsImageEdit] = useState<boolean>(false)
  const [selectedImage, setSelectedImage] = useState<ImageType | null>(null)

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    imageId: string,
  ) => {
    setMenuAnchorEl(event.currentTarget)
    setMenuImageId(imageId)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
    setMenuImageId(null)
  }
  const permissions = getProjectPermissions(caseDetails?.role)

  const [getImagePresignedUrl] = useLazyGetImagePresignedUrlQuery()
  const [uploadCaseImages] = useUploadCaseImagesMutation()

  const handleImageUpload = async (
    file: File,
    fileIndex: number,
    onSuccess?: () => void,
  ) => {
    try {
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, ' ')
      const presignedData = (await getImagePresignedUrl({
        caseId: caseId || '',
        fileName: sanitizedFileName,
      }).unwrap()) as PresignedUrlResponse

      toast.info('Uploading started.')

      await axios.put(presignedData.url, file, {
        headers: { 'Content-Type': file.type },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total,
            )
            updateProgress(fileIndex, percentCompleted)
          }
        },
      })

      toast.info('Processing started.')
      await uploadCaseImages({
        caseId: caseDetails?.id || '',
        key: presignedData?.key,
      }).unwrap()

      onSuccess?.()
    } catch (error) {
      if (error instanceof Error) {
        toast.error(`Upload failed: ${error.message}`)
      } else {
        toast.error('An unexpected error occurred during upload')
      }
    }
  }

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      const basePath = '/' + location.pathname.split('/')[1]

      const validFiles = acceptedFiles.filter((file) => {
        const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9._-]/g, ' ')
        const fileExtension = sanitizedFileName.split('.').pop()?.toLowerCase()

        if (
          !fileExtension ||
          !getSupportedImagetypes(basePath).includes(fileExtension as any)
        ) {
          toast.error('Invalid file type')
          return false
        }
        return true
      })

      let startIndex = 0
      if (validFiles.length > 0) {
        startIndex = onUploadInitiated?.(validFiles.length) ?? 0
      }

      let successfulUploads = 0
      let pollingStarted = false

      validFiles.forEach((file, index) => {
        handleImageUpload(file, startIndex + index, () => {
          successfulUploads++
          if (successfulUploads > 0 && !pollingStarted) {
            pollingStarted = true
            onUploadStart?.(validFiles.length)
          }
        })
      })
    },
    noClick: true,
  })

  const handleToggleSelectAll = () => {
    if (!images?.length) return

    if (selected.length === images.length) {
      images.forEach((img: ImageType) => {
        dispatch(
          toggleImageSelection({ imageId: img.id, caseId: caseDetails?.id }),
        )
      })
    } else {
      images.forEach((img: ImageType) => {
        if (!selected.includes(img.id)) {
          dispatch(
            toggleImageSelection({ imageId: img.id, caseId: caseDetails?.id }),
          )
        }
      })
    }
  }

  return (
    <>
      <Box mt={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'rgba(128, 128, 128, 0.55)' }}>
          <Tabs
            value={tab}
            onChange={(e, newValue) => setTab(newValue)}
            indicatorColor="primary"
          >
            <Tab
              label={t('images')}
              sx={{ textTransform: 'none', fontSize: '16px' }}
            />
            <Tab
              label={t('savedRecipes')}
              sx={{ textTransform: 'none', fontSize: '16px' }}
            />
            <Tab
              label={t('description')}
              sx={{ textTransform: 'none', fontSize: '16px' }}
            />
          </Tabs>
        </Box>

        {tab === 0 && (
          <>
            <Box
              bgcolor={'#121415'}
              minHeight={'653px'}
              width={'100%'}
              px={'79.04px'}
              py={'32px'}
              position={'relative'}
              {...getRootProps()}
            >
              <input {...getInputProps()} />

              <Box
                component="img"
                src={imageBg}
                alt="Background"
                sx={{
                  position: 'absolute',
                  top: '381px',
                  bottom: '112px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 0,
                  maxWidth: '100%',
                  height: 'auto',
                  pointerEvents: 'none',
                }}
              />

              <Box
                display="flex"
                justifyContent={
                  selected?.length > 0 ? 'space-between' : 'flex-end'
                }
                mt={2}
                mb={2}
                gap={2}
              >
                <MultipleImageActions
                  caseId={caseId}
                  refetch={refetch}
                  caseDetails={caseDetails}
                />
                <Button
                  label={
                    selected.length === images?.length
                      ? 'Deselect All'
                      : 'Select All'
                  }
                  variant="outlined"
                  textTransform="uppercase"
                  onClick={handleToggleSelectAll}
                />
                <FormControl size="small" sx={{ minWidth: 250 }}>
                  <Select
                    value={ordering}
                    onChange={(e) => setOrdering(e.target.value)}
                  >
                    {sortingData?.map((option) => (
                      <MenuItem key={option.id} value={option.value}>
                        {translation(option.name)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box display="flex" flexWrap="wrap" gap={3} sx={{ mt: 3 }}>
                {uploadingCount > 0 &&
                  Array.from({ length: uploadingCount }).map((_, idx) => {
                    const progress = progressArray[idx] || 0
                    return (
                      <Box
                        key={`upload-${idx}-${uploadingCount}`}
                        sx={{
                          width: '179.392px',
                          height: 150,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: '#2a2a2a',
                          position: 'relative',
                          p: 1,
                        }}
                      >
                        {progress >= 100 ? (
                          <ReactLoader
                            loaded={false}
                            lines={20}
                            length={20}
                            width={2}
                            radius={23}
                            color={'#c6c6c6'}
                            zIndex={9999}
                          />
                        ) : (
                          <ImageProgressBar value={progress} />
                        )}
                      </Box>
                    )
                  })}

                {images?.map((img: ImageType) => {
                  const names = img?.key_raw?.split('/') || []
                  const fileName =
                    names.length > 0 ? names[names.length - 1] : img.name

                  const isCalibrated = !!img?.calibrated_image

                  return (
                    <Box
                      key={img.id}
                      sx={{
                        position: 'relative',
                        width: '179.392px',
                        minHeight: 220,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Checkbox
                        sx={{
                          position: 'absolute',
                          left: 8,
                          top: 8,
                          height: 24,
                          width: 24,
                        }}
                        checked={selected?.includes(img.id)}
                        onChange={() => handleSelect(img.id)}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                        }}
                      >
                        <IconButton
                          sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.30)',
                            padding: '2px 4px',
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.30)',
                            },
                          }}
                          onClick={(e) => handleMenuOpen(e, img.id)}
                        >
                          <MoreHorizIcon sx={{ color: 'white' }} />
                        </IconButton>
                      </Box>
                      {menuImageId === img?.id && (
                        <ImageActionMenu
                          anchorEl={menuAnchorEl}
                          handleMenuClose={handleMenuClose}
                          refetch={refetch}
                          caseId={caseId}
                          imageId={menuImageId}
                          caseDetails={caseDetails}
                        />
                      )}
                      <Box
                        component="img"
                        src={
                          getDehazedImageUrl(img) ||
                          img?.latest_image ||
                          img?.calibrated_image ||
                          img?.image
                        }
                        alt={fileName}
                        sx={{
                          width: '179.392px',
                          height: 150,
                          objectFit: 'cover',
                          border: isCalibrated
                            ? '2px solid var(--color-accent-neonblue)'
                            : 'none',
                        }}
                      />
                      <Typography
                        color="white"
                        sx={{
                          mt: 1,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          color: 'white',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {fileName}
                      </Typography>
                      <Typography
                        sx={{
                          mt: 1,
                          color: 'white',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          textOverflow: 'ellipsis',
                          fontSize: '14px',
                          fontWeight: 400,
                        }}
                      >
                        {img?.note?.trim() ? img.note : '-'}
                      </Typography>
                      <Box
                        sx={{ mt: 1, display: 'flex', alignItems: 'center' }}
                      >
                        <Box
                          borderRadius="6.25rem"
                          bgcolor={
                            !isCalibrated && img?.type === imageType.RAW
                              ? '#FFB975'
                              : 'var(--color-accent-neonblue)'
                          }
                          height="1.5rem"
                          display="inline-flex"
                          alignItems="center"
                          justifyContent="center"
                          px={1.5}
                          color={'black'}
                          fontSize="0.8125rem"
                        >
                          {img?.type === imageType.PHOTO
                            ? (() => {
                                const ext = img.key_raw
                                  ?.split('.')
                                  .pop()
                                  ?.toUpperCase()
                                return ext || 'Photo'
                              })()
                            : img?.type !== imageType.RAW
                            ? 'Optishade'
                            : !isCalibrated
                            ? 'Not Calibrated'
                            : img?.calibrate_meta?.protocol
                            ? `Calibrated L${img.calibrate_meta.protocol}`
                            : 'Calibrated'}
                        </Box>
                      </Box>
                      {permissions?.canEditImageNote && (
                        <Box
                          sx={{
                            mt: 1,
                            display: 'flex',
                            justifyContent: 'flex-start',
                          }}
                        >
                          <IconButton
                            sx={{ p: 0, minWidth: 'auto' }}
                            onClick={() => {
                              setSelectedImage(img)
                              setIsImageEdit(true)
                            }}
                          >
                            <img src={editIcon} alt="error" color="#90CAF9" />
                          </IconButton>
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Box>
            </Box>
          </>
        )}

        {tab === 1 && (
          <RecipeDetailTable caseId={caseId} caseDetails={caseDetails} />
        )}
        {tab === 2 && (
          <RecipeDescription
            caseDetails={caseDetails}
            caseId={caseId}
            refetch={refetch}
          />
        )}
      </Box>
      {isImageEdit && (
        <ImageEditContainer
          open={isImageEdit}
          onClose={() => setIsImageEdit(false)}
          caseId={caseId}
          initialData={selectedImage as any}
          refetchRecipe={refetch}
        />
      )}
    </>
  )
}

export default ProjectImageGallery
